import { Controller } from "@hotwired/stimulus"

// Inline front-end validation for every form in the app.
//
// Mounted once on <body>, so it covers forms rendered now and forms that Turbo
// swaps in later. It hooks the native `invalid` event: the browser still does
// the checking (required, pattern, type, min/max), we just suppress its ugly
// bubble and render the message under the field instead — matching the
// server-rendered `field_error` styling, so a blank field looks the same
// whether the browser or the model caught it.
//
// Colours are set inline rather than via utility classes so this keeps working
// regardless of what the CSS build happened to include.
const ERROR_CLASS = "mt-1 text-xs text-red-600"
const ERROR_ATTR = "data-validation-error"
const INVALID_BORDER = "#dc2626"

export default class extends Controller {
  connect() {
    // `invalid` does not bubble — capture it on the way down.
    this.onInvalid = this.handleInvalid.bind(this)
    this.onEdit = this.handleEdit.bind(this)
    this.onSubmit = this.handleSubmit.bind(this)

    this.element.addEventListener("invalid", this.onInvalid, true)
    this.element.addEventListener("input", this.onEdit)
    this.element.addEventListener("change", this.onEdit)
    this.element.addEventListener("submit", this.onSubmit, true)
  }

  disconnect() {
    this.element.removeEventListener("invalid", this.onInvalid, true)
    this.element.removeEventListener("input", this.onEdit)
    this.element.removeEventListener("change", this.onEdit)
    this.element.removeEventListener("submit", this.onSubmit, true)
  }

  // A fresh submit attempt starts a fresh set of messages, and the first
  // offending field is the one we focus.
  handleSubmit(event) {
    this.clearAll(event.target)
    this.focusPending = true
  }

  handleInvalid(event) {
    const control = event.target
    if (!control || control.disabled) return

    event.preventDefault() // suppress the native bubble
    this.showError(control, this.messageFor(control))

    if (this.focusPending) {
      this.focusPending = false
      control.focus({ preventScroll: true })
      control.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  // Read `validity.valid` rather than calling checkValidity(): the latter
  // DISPATCHES an invalid event, which would pop an error under the field while
  // the user is still halfway through typing it.
  handleEdit(event) {
    const control = event.target
    if (!control || !control.validity) return
    if (control.validity.valid) this.clearError(control)
  }

  messageFor(control) {
    if (!control.validity.valueMissing) return control.validationMessage

    return `${this.labelFor(control)} is required`
  }

  // Line-grid controls carry no <label> — they lean on aria-label, then the
  // placeholder, so the message still names the field rather than saying
  // "This field".
  labelFor(control) {
    const label = control.labels && control.labels[0]
    const text = label
      ? this.ownText(label)
      : control.getAttribute("aria-label") || control.getAttribute("placeholder")
    const cleaned = (text || "").replace(/\*/g, "").replace(/\s+/g, " ").trim().replace(/:+$/, "")

    return cleaned || "This field"
  }

  // Some labels WRAP their control ("<label>Category <select>…</select></label>"),
  // so textContent would drag every <option> into the message. Take only the
  // label's own text nodes.
  ownText(label) {
    const own = Array.from(label.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent)
      .join(" ")

    return own.trim() ? own : label.textContent
  }

  showError(control, message) {
    this.clearError(control)

    const paragraph = document.createElement("p")
    paragraph.className = ERROR_CLASS
    paragraph.setAttribute(ERROR_ATTR, "")
    paragraph.setAttribute("data-validation-error-for", this.keyFor(control))
    paragraph.setAttribute("role", "alert")
    paragraph.textContent = message

    this.place(paragraph, control)
    control.setAttribute("aria-invalid", "true")
    control.dataset.previousBorderColor = control.style.borderColor
    control.style.borderColor = INVALID_BORDER
  }

  // A line row is a `grid grid-cols-12` — dropping the message in as a sibling
  // makes it a GRID CELL, so it lands beside the field and shoves the rest of
  // the row (and its heading) out of alignment. In a grid, append it to the row
  // spanning every column instead: it sits under the fields, in flow, and
  // nothing shifts. Anywhere else, straight after the control is already right.
  place(paragraph, control) {
    const parent = control.parentElement
    if (!parent) return

    if (window.getComputedStyle(parent).display.includes("grid")) {
      paragraph.style.gridColumn = "1 / -1"
      paragraph.style.margin = "0"
      parent.appendChild(paragraph)
    } else {
      control.insertAdjacentElement("afterend", paragraph)
    }
  }

  clearError(control) {
    const scope = control.form || this.element
    const existing = scope.querySelector(`[data-validation-error-for="${this.keyFor(control)}"]`)
    if (existing) existing.remove()

    control.removeAttribute("aria-invalid")
    if ("previousBorderColor" in control.dataset) {
      control.style.borderColor = control.dataset.previousBorderColor
      delete control.dataset.previousBorderColor
    }
  }

  // Line-grid controls are hand-written HTML with a name but no id.
  keyFor(control) {
    return control.id || control.name || ""
  }

  clearAll(form) {
    if (!form || typeof form.querySelectorAll !== "function") return

    form.querySelectorAll(`[${ERROR_ATTR}]`).forEach((node) => node.remove())
    form.querySelectorAll("[aria-invalid]").forEach((control) => {
      control.removeAttribute("aria-invalid")
      control.style.borderColor = control.dataset.previousBorderColor || ""
      delete control.dataset.previousBorderColor
    })
  }
}
